import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ref, get } from "firebase/database";
import { db } from "./firebase/config";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { createSlug } from "./utils/slugUtils";

const NewsDetailScreen = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const newsRef = ref(db, "news");
        const snapshot = await get(newsRef);

        if (snapshot.exists()) {
          const newsData = snapshot.val();
          let foundArticle = null;
          let articleId = null;

          Object.entries(newsData).forEach(([id, data]) => {
            const articleSlug = createSlug(data.title);
            if (articleSlug === slug) {
              foundArticle = { id, ...data };
              articleId = id;
            }
          });

          if (foundArticle) {
            setArticle(foundArticle);
            fetchRelatedArticles(newsData, articleId);
          }
        }
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchRelatedArticles = (newsData, currentId) => {
    try {
      const articlesArray = Object.entries(newsData)
        .map(([key, value]) => ({
          id: key,
          ...value,
          slug: createSlug(value.title),
        }))
        .filter((item) => item.id !== currentId)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 3);

      setRelatedArticles(articlesArray);
    } catch (error) {
      console.error("Error processing related articles:", error);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Sanitize content: remove DOCTYPE, html, head, body wrappers and duplicate h1 tags
  const sanitizeArticleContent = (content) => {
    if (!content) return "";

    let cleaned = content;

    // Remove full HTML document wrappers
    const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch && bodyMatch[1]) {
      cleaned = bodyMatch[1];
    }

    // Remove any remaining DOCTYPE, html, head tags
    cleaned = cleaned
      .replace(/<!DOCTYPE[^>]*>/gi, "")
      .replace(/<\/?html[^>]*>/gi, "")
      .replace(/<head[\s\S]*?<\/head>/gi, "")
      .replace(/<\/?body[^>]*>/gi, "");

    // Remove h1 tags from content since we render h1 separately
    cleaned = cleaned.replace(/<h1[^>]*>[\s\S]*?<\/h1>/gi, "");

    return DOMPurify.sanitize(cleaned.trim());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Không tìm thấy bài viết
          </h1>
          <p className="text-gray-600 mb-6">
            Bài viết bạn đang tìm kiếm không tồn tại hoặc đã bị xóa.
          </p>
          <Link
            to="/news"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Quay lại trang tin tức
          </Link>
        </div>
      </div>
    );
  }

  const articleUrl = `https://autowork.com.vn/news/${slug}`;
  const publishedDate = article.createdAt
    ? new Date(article.createdAt).toISOString()
    : new Date().toISOString();
  const modifiedDate = article.updatedAt
    ? new Date(article.updatedAt).toISOString()
    : publishedDate;

  return (
    <>
      <Helmet>
        <title>{article.title} | AutoWork</title>
        <meta name="description" content={article.description} />
        <meta
          name="keywords"
          content={`${article.title}, tin tức AI, ${
            article.category || "công nghệ"
          }, autowork`}
        />
        <meta property="og:title" content={`${article.title} | AutoWork`} />
        <meta property="og:description" content={article.description} />
        <meta property="og:type" content="article" />
        {article.imageUrl && (
          <meta property="og:image" content={article.imageUrl} />
        )}
        <meta property="og:url" content={articleUrl} />
        <meta property="og:site_name" content="AutoWork" />
        <meta property="og:locale" content="vi_VN" />
        <meta property="article:published_time" content={publishedDate} />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="article:author" content="AutoWork" />
        <meta
          property="article:section"
          content={article.category || "Công nghệ"}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${article.title} | AutoWork`} />
        <meta name="twitter:description" content={article.description} />
        {article.imageUrl && (
          <meta name="twitter:image" content={article.imageUrl} />
        )}
        <link rel="canonical" href={articleUrl} />

        {/* BlogPosting Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.description,
            image: article.imageUrl,
            datePublished: publishedDate,
            dateModified: modifiedDate,
            author: {
              "@type": "Organization",
              name: "AutoWork",
              url: "https://autowork.com.vn",
              logo: {
                "@type": "ImageObject",
                url: "https://autowork.com.vn/logo1.png",
              },
            },
            publisher: {
              "@type": "Organization",
              name: "AutoWork",
              logo: {
                "@type": "ImageObject",
                url: "https://autowork.com.vn/logo1.png",
              },
            },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": articleUrl,
            },
            articleSection: article.category || "Công nghệ",
            inLanguage: "vi",
          })}
        </script>

        {/* BreadcrumbList Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Trang chủ",
                item: "https://autowork.com.vn",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Tin tức",
                item: "https://autowork.com.vn/news",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
              },
            ],
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 pt-20 md:pt-24 px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb Navigation */}
        <nav
          className="max-w-4xl mx-auto mb-4"
          aria-label="Breadcrumb"
        >
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-blue-600 transition-colors">
                Trang chủ
              </Link>
            </li>
            <li>
              <span className="mx-1">/</span>
            </li>
            <li>
              <Link
                to="/news"
                className="hover:text-blue-600 transition-colors"
              >
                Tin tức
              </Link>
            </li>
            <li>
              <span className="mx-1">/</span>
            </li>
            <li className="text-gray-900 font-medium truncate max-w-xs">
              {article.title}
            </li>
          </ol>
        </nav>

        <article className="max-w-4xl mx-auto bg-white rounded-lg shadow-md overflow-hidden">
          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              width={800}
              height={400}
              fetchPriority="high"
              className="w-full h-64 md:h-96 object-cover"
            />
          )}

          <div className="p-6 md:p-8">
            {/* Article Header */}
            <div className="text-sm text-gray-500 mb-4 flex items-center">
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <time dateTime={publishedDate}>
                {formatDate(article.createdAt)}
              </time>
            </div>

            {/* H1 Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div
                dangerouslySetInnerHTML={{
                  __html: sanitizeArticleContent(article.content),
                }}
              />
            </div>

            {/* Author Section */}
            <div className="mt-10 pt-6 border-t border-gray-200">
              <div className="flex items-center">
                <img
                  src="https://autowork.com.vn/logo1.png"
                  alt="AutoWork"
                  className="w-12 h-12 rounded-full object-contain bg-gray-100 p-1"
                />
                <div className="ml-4">
                  <p className="text-sm font-semibold text-gray-900">
                    AutoWork Team
                  </p>
                  <p className="text-sm text-gray-500">
                    Nền tảng AI tự động hóa marketing — Tự động viết và đăng bài
                    Facebook, WordPress
                  </p>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="max-w-4xl mx-auto mt-12 pb-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Bài viết liên quan
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedArticles.map((relatedArticle) => (
                <Link
                  key={relatedArticle.id}
                  to={`/news/${relatedArticle.slug}`}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {relatedArticle.imageUrl && (
                    <img
                      src={relatedArticle.imageUrl}
                      alt={relatedArticle.title}
                      width={400}
                      height={192}
                      loading="lazy"
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-4">
                    <div className="text-sm text-gray-500 mb-2">
                      {formatDate(relatedArticle.createdAt)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                      {relatedArticle.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NewsDetailScreen;
